import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { useTranslation } from "../contexts/I18nContext";
import { PageLoader } from "../components/ui/page-loader";
import {
  Database,
  Download,
  Upload,
  Clock,
  HardDrive,
  RefreshCw,
  Shield,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Loader2,
  ArrowLeft,
  LogOut
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function BackupPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [backupStatus, setBackupStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreDialog, setRestoreDialog] = useState({ open: false, filename: null });
  const [admin, setAdmin] = useState(null);

  const token = localStorage.getItem("superAdminToken");

  useEffect(() => {
    if (!token) {
      navigate("/admin");
      return;
    }
    // Get admin info
    const storedAdmin = localStorage.getItem("superAdmin");
    if (storedAdmin) setAdmin(JSON.parse(storedAdmin));
  }, [token, navigate]);

  const fetchBackups = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/backups`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setBackupStatus(data);
      } else {
        handleApiError({ message: t("backup.loadError") });
      }
    } catch (error) {
      handleApiError(error, t("auth.connectionFailedNetwork"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBackups();
  }, []);

  const createBackup = async () => {
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/backups`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        showSuccess(`${t("backup.backupCreated")}: ${data.filename} (${data.size_mb} MB)`);
        fetchBackups();
      } else {
        handleApiError({ message: data.detail || t("backup.backupError") });
      }
    } catch (error) {
      handleApiError(error, t("auth.connectionFailedNetwork"));
    } finally {
      setCreating(false);
    }
  };

  const restoreBackup = async (filename) => {
    setRestoring(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/backups/restore?filename=${encodeURIComponent(filename)}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        showSuccess(t("backup.restoreSuccess"));
        fetchBackups();
      } else {
        handleApiError({ message: data.detail || t("backup.restoreError") });
      }
    } catch (error) {
      handleApiError(error, t("auth.connectionFailedNetwork"));
    } finally {
      setRestoring(false);
      setRestoreDialog({ open: false, filename: null });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleLogout = () => {
    localStorage.removeItem("superAdminToken");
    localStorage.removeItem("superAdmin");
    navigate("/admin");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900" data-testid="backup-page">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/admin/dashboard")}
              className="text-slate-300 hover:bg-slate-700"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <Database className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">{t("backup.title")}</h1>
              <p className="text-sm text-slate-400">{t("backup.subtitle")}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-slate-300 text-sm">
              {admin?.nome} {admin?.cognome}
            </span>
            <Button variant="outline" size="sm" onClick={handleLogout} className="border-slate-600 text-slate-300 hover:bg-slate-700">
              <LogOut className="h-4 w-4 mr-2" /> {t("common.logout")}
            </Button>
          </div>
        </div>
      </header>

      <main className="p-6 space-y-6">
        {/* Action Buttons */}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={fetchBackups} className="border-slate-600 text-slate-300 hover:bg-slate-700">
            <RefreshCw className="h-4 w-4 mr-2" />
            {t("common.refresh")}
          </Button>
          <Button onClick={createBackup} disabled={creating} className="bg-purple-600 hover:bg-purple-700">
            {creating ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            {t("backup.createNow")}
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-4">
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-slate-300">{t("backup.systemStatus")}</CardTitle>
            <Shield className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <span className="text-lg font-bold text-white">{t("backup.active")}</span>
            </div>
            <p className="text-xs text-slate-400 mt-1">
              {backupStatus?.schedule}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-slate-300">{t("backup.totalBackups")}</CardTitle>
            <Database className="h-4 w-4 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white">{backupStatus?.total_backups || 0}</div>
            <p className="text-xs text-slate-400">
              {backupStatus?.total_size_mb?.toFixed(2) || 0} {t("backup.totalMb")}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-slate-300">{t("backup.retention")}</CardTitle>
            <Calendar className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white">{backupStatus?.retention_days || 7} {t("backup.retentionDays")}</div>
            <p className="text-xs text-slate-400">
              {t("backup.retentionInfo")}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-slate-300">{t("backup.lastBackup")}</CardTitle>
            <Clock className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            {backupStatus?.latest_backup ? (
              <>
                <div className="text-lg font-bold text-white">
                  {backupStatus.latest_backup.size_mb} MB
                </div>
                <p className="text-xs text-slate-400">
                  {formatDate(backupStatus.latest_backup.created_at)}
                </p>
              </>
            ) : (
              <div className="text-sm text-slate-400">{t("backup.noBackup")}</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Info Card */}
      <Card className="bg-slate-800/50 border-purple-500/30">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <HardDrive className="h-8 w-8 text-purple-500 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-white">{t("backup.autoBackups")}</h3>
              <p className="text-sm text-slate-300 mt-1">
                {t("backup.autoBackupsInfo")}
              </p>
              <p className="text-sm text-slate-400 mt-2">
                <strong className="text-slate-300">{t("backup.backupDir")}:</strong> {backupStatus?.backup_dir}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Backup List */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white">{t("backup.backupList")}</CardTitle>
          <CardDescription className="text-slate-400">
            {t("backup.backupListSubtitle")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {backupStatus?.backups?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700">
                  <TableHead className="text-slate-300">{t("backup.fileName")}</TableHead>
                  <TableHead className="text-slate-300">{t("backup.type")}</TableHead>
                  <TableHead className="text-slate-300">{t("backup.size")}</TableHead>
                  <TableHead className="text-slate-300">{t("backup.createdAt")}</TableHead>
                  <TableHead className="text-right text-slate-300">{t("common.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {backupStatus.backups.map((backup) => (
                  <TableRow key={backup.filename} className="border-slate-700">
                    <TableCell className="font-mono text-sm text-slate-300">
                      {backup.filename}
                    </TableCell>
                    <TableCell>
                      <Badge variant={backup.type === "scheduled" ? "secondary" : "default"}>
                        {backup.type === "scheduled" ? t("backup.auto") : t("backup.manual")}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-300">{backup.size_mb} MB</TableCell>
                    <TableCell className="text-slate-300">{formatDate(backup.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setRestoreDialog({ open: true, filename: backup.filename })}
                        disabled={restoring}
                        className="border-slate-600 text-slate-300 hover:bg-slate-700"
                      >
                        <Upload className="h-4 w-4 mr-1" />
                        {t("backup.restore")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-slate-400">
              <Database className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{t("backup.noBackupsAvailable")}</p>
              <p className="text-sm">{t("backup.createFirstBackup")}</p>
            </div>
          )}
        </CardContent>
      </Card>
      </main>

      {/* Restore Confirmation Dialog */}
      <AlertDialog open={restoreDialog.open} onOpenChange={(open) => setRestoreDialog({ ...restoreDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              {t("backup.confirmRestore")}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                {t("backup.restoreFrom")}
              </p>
              <p className="font-mono bg-muted p-2 rounded text-sm">
                {restoreDialog.filename}
              </p>
              <p className="text-yellow-600 font-medium">
                {t("backup.restoreWarning")}
              </p>
              <p>
                {t("backup.restoreBackupNote")}
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={restoring}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => restoreBackup(restoreDialog.filename)}
              disabled={restoring}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              {restoring ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              {t("backup.confirmRestore")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
